/**
 * planPeriod — the ONE place that says when a goal plan starts, ends, and how far along it is.
 *
 * Inputs are only the user's settings: `planStartMonth` (month the goal was set) and
 * `planLength` (months). Everything else — deadline, roadmap months, 「n / N ヶ月目」,
 * 「n% 経過」, 残り日数 — is derived here so Goals / Home / Settings / GoalEdit / chart
 * cannot disagree (docs/MY_TASKS/TASK_GOALS_PROGRESS_LOGIC.md).
 *
 * Dates are JST calendar days: the salons are in Japan and the API's `dataAsOf` is JST.
 */

import { jstToday } from './jstDate';

const DEFAULT_PLAN_LENGTH = 6;
const MAX_PLAN_LENGTH = 24;
// The goal screen picks an END MONTH (齋藤さん 2026-10-16); at most this many months from the
// start month — the same cap as a stored plan, so a 2-year goal (2026-10 → 2028-09) is pickable.
export const MAX_PICK_MONTHS = MAX_PLAN_LENGTH;
const MONTH_RE = /^\d{4}-\d{2}$/;

export { jstToday };

export function parseMonthKey(monthKey) {
  if (!monthKey || typeof monthKey !== 'string' || !MONTH_RE.test(monthKey)) return null;
  const [y, m] = monthKey.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12) return null;
  return { year: y, month: m };
}

export function monthKeyOffset(monthKey, delta) {
  const p = parseMonthKey(monthKey);
  if (!p) return null;
  const total = p.year * 12 + (p.month - 1) + delta;
  const y = Math.floor(total / 12);
  const m = (total % 12) + 1;
  return `${y}-${String(m).padStart(2, '0')}`;
}

export function monthDiff(fromKey, toKey) {
  const a = parseMonthKey(fromKey);
  const b = parseMonthKey(toKey);
  if (!a || !b) return null;
  return (b.year - a.year) * 12 + (b.month - a.month);
}

export function daysInMonth(monthKey) {
  const p = parseMonthKey(monthKey);
  if (!p) return null;
  return new Date(Date.UTC(p.year, p.month, 0)).getUTCDate();
}

/** Last calendar day of a month as 'YYYY-MM-DD'. */
export function lastDayOfMonth(monthKey) {
  const d = daysInMonth(monthKey);
  return d ? `${monthKey}-${String(d).padStart(2, '0')}` : null;
}

/** 'YYYY-MM' → 'YYYY年M月'; null when the key is not a month. */
export function formatMonthLabel(monthKey) {
  const p = parseMonthKey(monthKey);
  return p ? `${p.year}年${p.month}月` : null;
}

/** planLength for a plan running start … end inclusive; null when end is before start. */
export function planLengthBetween(startMonth, endMonth) {
  const d = monthDiff(startMonth, endMonth);
  return d != null && d >= 0 ? d + 1 : null;
}

/**
 * End months the picker may offer for `startMonth`: never before the start, never before
 * `todayMonth` (a deadline already passed drops the chart's「必要」line — goalTrajectory
 * skips stale deadlines), and at most MAX_PICK_MONTHS from the start. Empty when the start
 * is so far back that no end month within the cap is still ahead.
 */
export function pickablePlanEndMonths(startMonth, todayMonth) {
  if (!parseMonthKey(startMonth) || !parseMonthKey(todayMonth)) return [];
  const out = [];
  for (let i = 0; i < MAX_PICK_MONTHS; i += 1) {
    const mk = monthKeyOffset(startMonth, i);
    if (monthDiff(todayMonth, mk) >= 0) out.push(mk);
  }
  return out;
}

export function normalizePlanLength(raw) {
  const n = Number(raw);
  return Number.isFinite(n) && n >= 1 && n <= MAX_PLAN_LENGTH ? Math.round(n) : DEFAULT_PLAN_LENGTH;
}

/** Whole-day difference b - a for two 'YYYY-MM-DD' strings (UTC math, no DST). */
function dayDiff(aYmd, bYmd) {
  const a = Date.parse(`${aYmd}T00:00:00Z`);
  const b = Date.parse(`${bYmd}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86400000);
}

export function formatGoalDateLabel(ymd) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  return `${y}年${m}月${d}日`;
}

/**
 * Start month of the plan. Stored `planStartMonth` wins. Older docs without it are
 * backfilled from what they do have, in order: goalDate → createdAt → today. The
 * backfill is deterministic, so a doc without the field shows the same period every time.
 */
export function resolvePlanStartMonth(goals, todayYmd = jstToday()) {
  if (!goals) return null;
  if (MONTH_RE.test(goals.planStartMonth || '')) return goals.planStartMonth;
  const len = normalizePlanLength(goals.planLength);
  // Legacy goalDate was written as (day the goal was set) + planLength months, same day of
  // month — so the month it was set is goalDate's month minus planLength.
  const goalMonth = typeof goals.goalDate === 'string' ? goals.goalDate.slice(0, 7) : null;
  if (MONTH_RE.test(goalMonth || '')) return monthKeyOffset(goalMonth, -len);
  const created = typeof goals.createdAt === 'string' ? goals.createdAt.slice(0, 7) : null;
  if (MONTH_RE.test(created || '')) return created;
  return todayYmd.slice(0, 7);
}

/**
 * @param {object|null} goals  saved goals doc (planStartMonth, planLength, goalDate, createdAt)
 * @param {object} [opts]
 * @param {string} [opts.viewMonth]  month on screen ('YYYY-MM'); defaults to today's month
 * @param {string} [opts.today]      JST 'YYYY-MM-DD'; defaults to jstToday()
 * @param {string} [opts.asOf]       'YYYY-MM-DD' the data reaches (API dataAsOf); defaults to today
 * @returns {object|null} null when there is no goal at all
 */
export function resolvePlanPeriod(goals, opts = {}) {
  if (!goals) return null;
  const today = opts.today || jstToday();
  const asOf = opts.asOf && /^\d{4}-\d{2}-\d{2}$/.test(opts.asOf) ? opts.asOf : today;
  const todayMonth = today.slice(0, 7);
  const viewMonth = MONTH_RE.test(opts.viewMonth || '') ? opts.viewMonth : todayMonth;

  const planLength = normalizePlanLength(goals.planLength);
  const startMonth = resolvePlanStartMonth(goals, today);
  if (!startMonth) return null;
  // A「Nヶ月プラン」set in month S is the N months S … S+N-1: the month the goal was set is
  // 「1 / N ヶ月目」(owner 2026-10-04). The deadline is the last day of S+N-1, and the Home
  // chart reads the same period (resolveGoalMonthKey), so both screens end on one month.
  const endMonth = monthKeyOffset(startMonth, planLength - 1);
  const goalDate = lastDayOfMonth(endMonth);
  const monthKeys = Array.from({ length: planLength }, (_, i) => monthKeyOffset(startMonth, i));

  const viewOffset = monthDiff(startMonth, viewMonth);
  const isBeforePlan = viewOffset < 0;
  const isAfterPlan = viewOffset >= planLength;
  const phaseIndex = Math.min(planLength - 1, Math.max(0, viewOffset));

  // 「n% 経過」: whole months done + fraction of the viewed month that the data covers.
  // Past months count as full; future months as zero.
  let dayFraction = 0;
  const cmp = monthDiff(viewMonth, asOf.slice(0, 7));
  if (cmp > 0) dayFraction = 1;
  else if (cmp === 0) dayFraction = Number(asOf.slice(8, 10)) / daysInMonth(viewMonth);
  const elapsedRaw = isBeforePlan ? 0 : isAfterPlan ? 1 : (viewOffset + dayFraction) / planLength;
  const elapsedPct = Math.round(Math.min(1, Math.max(0, elapsedRaw)) * 100);

  const daysToGoal = Math.max(0, dayDiff(today, goalDate) ?? 0);

  return {
    startMonth,
    endMonth,
    goalDate,
    goalDateLabel: formatGoalDateLabel(goalDate),
    monthKeys,
    planLength,
    viewMonth,
    viewOffset,
    phaseIndex,
    phaseLabel: `${phaseIndex + 1} / ${planLength} ヶ月目`,
    elapsedPct,
    daysToGoal,
    isBeforePlan,
    isAfterPlan,
    today,
    asOf,
  };
}
